import React, { useContext, useState } from 'react';
import css from 'styled-jsx/css';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { formatter } from '@yupwego/string-fns';
import format from 'date-fns/format';
import fetch from 'isomorphic-unfetch';

import { colors } from '../common/Theme';
import MatomoAsyncProvider from '../common/services/MatomoProvider';
import Overlay from '../common/elements/Overlay';
import Button from '../common/elements/Button';

const WIDTH = '660px';

const cancelSubscriptionStyles = css`
  .container {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    justify-content: center;
    align-items: center;
    background: ${colors.backdrop};
  }

  .content {
    padding: 40px 0 24px;
    background-color: white;
    box-shadow: 0 0 16px 0 ${colors.boxShadow};
    box-sizing: border-box;
  }

  .actions {
    padding-bottom: 40px;
    text-align: center;
  }

  .title {
    margin: 0 0 16px;
    padding: 0;
    color: ${colors.ywgPrimary};
    font-size: 2.25rem;
    text-align: center;
  }

  .subtitle {
    margin: 16px 0;
    font-size: 1.125rem;
    text-align: center;
  }

  .key,
  .value {
    margin: 0;
    padding: 0;
  }

  .key {
    margin-bottom: 8px;
    vertical-align: top;
  }

  .value {
    margin-bottom: 20px;
    font-weight: 700;
  }

  .infos {
    margin: 0 0 24px;
    font-size: 1.125rem;
  }

  .travelers-list {
    padding: 0;
    list-style: none;
  }

  .traveler-item {
    display: flex;
    align-items: center;
  }

  .traveler-name {
    text-align: right;
    font-weight: 700;
  }

  .traveler-name::after {
    padding: 0 5px;
    content: '-';
  }

  .traveler-details {
    text-align: left;
    font-size: 0.778em;
  }

  .cancel-reason {
    display: flex;
    width: 100%;
    flex-direction: column;
    background-color: ${colors.ywgPaleGrey};
    box-sizing: border-box;
  }

  .cancel-reason-list {
    margin: 12px 0 24px;
    padding: 0;
  }

  .cancel-reason-item {
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    list-style: none;
  }

  .cancel-reason-label {
    margin-left: 8px;
    padding: 10px 0;
    cursor: pointer;
  }

  .cancel-button {
    margin-top: 20px;
    font-size: 0.875rem;
    text-decoration: underline;
    background: none;
    border: none;
  }

  .cancel-button:hover,
  .cancel-button:active,
  .cancel-button:focus {
    text-decoration: none;
  }

  .item-spacing {
    padding-left: 20px;
    padding-right: 20px;
  }

  .canceled-title-valid {
    position: relative;
    margin: 20px 0 0;
    font-size: 2rem;
    color: ${colors.valid};
  }

  .canceled-title-valid::before {
    display: block;
    margin: 0 auto 24px;
    width: 80px;
    height: 80px;
    content: '';
    background-color: currentColor;
    border-radius: 50%;
  }

  .canceled-text {
    margin: 24px 0;
    font-size: 1.125rem;
    text-align: center;
  }

  .canceled-text::after {
    display: block;
    margin-top: 15px;
    content: '🌍 👍';
    font-size: 2rem;
    line-height: 1;
  }

  @media (min-width: ${WIDTH}) {
    .container {
      display: flex;
      display: none;
    }

    .content {
      display: flex;
      width: ${WIDTH};
      flex-direction: column;
    }

    .actions {
      padding-bottom: 0;
    }

    .key,
    .value {
      display: inline-block;
      margin-bottom: 8px;
      width: 50%;
      box-sizing: border-box;
    }

    .key {
      text-align: right;
    }

    .key::after {
      padding: 0 5px;
      content: ':';
    }

    .date,
    .travelers-list {
      margin-bottom: 24px;
    }

    .travelers,
    .travelers-list {
      padding: 0;
      width: 100%;
    }

    .travelers {
      text-align: center;
    }

    .travelers-value {
      line-height: 1.7;
    }

    .traveler-name,
    .traveler-details {
      width: 50%;
    }

    .item-spacing {
      padding-left: 100px;
      padding-right: 100px;
    }

    .canceled-text {
      margin-left: auto;
      margin-right: auto;
      max-width: 400px;
    }
  }
`;

const confirmButtonStyles = css.resolve`
  .confirm-button {
    margin-left: auto;
    margin-right: auto;
  }
`;

const cancelOverlayStyles = css.resolve`
  .cancel-overlay {
    padding-left: 0;
    padding-right: 0;
  }
`;

const cancelSubscriptionReasonsDefault = 'unknown';
const cancelSubscriptionReasons = [
  {
    id: 'dissatisfied',
    text: 'Je ne suis pas satisfait du service proposé'
  },
  {
    id: 'switching',
    text: 'Nous avons trouvé une assurance qui nous convient mieux'
  },
  {
    id: cancelSubscriptionReasonsDefault,
    text: 'Je ne souhaite pas vous dire la raison'
  }
];

const cancelInactiveSubscriptionReasons = [
  {
    id: 'don-t-go',
    text: 'Nous ne partons plus !'
  }
];

const cancelActiveSubscriptionReasons = [
  {
    id: 'home',
    text: 'Nous sommes bien rentrés chez nous !'
  }
];

const isSubscriber = (subscriber, traveler) => {
  return (
    subscriber.firstname === traveler.firstname &&
    subscriber.lastname === traveler.lastname &&
    subscriber.birthday === traveler.birthday
  );
};

const CancelSubscription = ({ subscription, onClose, onCanceled }) => {
  const tracking = useContext(MatomoAsyncProvider.Context);
  const [subscriptionCanceled, setSubcriptionCanceled] = useState(false);
  const [cancelReason, setCancelReason] = useState(
    cancelSubscriptionReasonsDefault
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const cancelSubscription = async () => {
    if (submitting === true) {
      return false;
    }
    setSubmitting(true);
    const response = await fetch(
      `/api/subscriptions/${subscription.id}?reason=${cancelReason}`,
      {
        method: 'DELETE',
        credentials: 'include'
      }
    );
    if (response.ok) {
      onCanceled(subscription);
      setSubcriptionCanceled(true);
    } else {
      setError(response.status);
      setSubmitting(false);
    }
  };
  const cancelReasonChanged = e => {
    const reason = e.currentTarget.value;
    setCancelReason(reason);
    tracking.trackEvent('Subscription', 'Cancel', reason);
  };
  return (
    <>
      <Overlay
        onClose={onClose}
        contentClassName={classnames(
          'cancel-overlay',
          cancelOverlayStyles.className
        )}
      >
        {(overlayClassName, closeOverlay) => {
          const reasonsToAdd =
            subscription.status === 'ACTIVE'
              ? cancelActiveSubscriptionReasons
              : cancelInactiveSubscriptionReasons;
          const reasons = [...reasonsToAdd, ...cancelSubscriptionReasons];
          return !subscriptionCanceled ? (
            <>
              <h2 className="title item-spacing">Arrêt de l’assurance</h2>
              <dl className="infos item-spacing">
                <dt className="country key">Pays</dt>
                <dd className="country value">
                  {subscription.stay.destination}
                </dd>
                <dt className="date key">Date de fin d’assurance</dt>
                <dd className="date value">
                  {formatter(format(new Date(), 'yyyy-MM-dd'))} - 23h59
                </dd>
                <dt className="travelers key">Voyageurs concernés</dt>
                <dd className="travelers travelers-value value">
                  <ul className="travelers-list">
                    {subscription.beneficiaries.map(beneficiary => (
                      <li
                        key={`cancel-subscription-traveler-${beneficiary.key}`}
                        className="traveler-item"
                      >
                        <span className="traveler-name">
                          {beneficiary.firstname} {beneficiary.lastname}
                        </span>{' '}
                        <span className="traveler-details">
                          {formatter(beneficiary.birthday)}{' '}
                          {isSubscriber(
                            subscription.subscriber,
                            beneficiary
                          ) ? (
                            <>(Souscripteur)</>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </dd>
              </dl>
              <div className="cancel-reason item-spacing">
                <h3 className="subtitle">Quelle est la raison de l’arrêt ?</h3>
                <ul className="cancel-reason-list">
                  {reasons.map(reason => (
                    <li
                      key={`profile-cancel-subscription-reason-${reason.id}`}
                      className="cancel-reason-item"
                    >
                      <input
                        type="radio"
                        id={reason.id}
                        name="cancel-subscription-reason"
                        value={reason.id}
                        onChange={cancelReasonChanged}
                      />
                      <label
                        className="cancel-reason-label"
                        htmlFor={reason.id}
                      >
                        {reason.text}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="actions item-spacing">
                {error ? (
                  <p>
                    Une erreur technique a empêché l’annulation de l’abonnement.
                    Si l&apos;erreur persiste, contactez-nous
                    <a
                      href={`mailto:contact@yupwego.com?subject=Erreur%20lors%20de%20l%27annulation%20de%20la%20souscription&body=Bonjour,%20je%20n%27ai%20pas%20pu%20annuler%20ma%20souscription.%20Code%20erreur%20${error}%20pour%20la%20souscription%20${subscription.id}`}
                    >
                      📧 contact@yupwego.com
                    </a>
                    .
                  </p>
                ) : (
                  <Button
                    className={classnames(
                      'confirm-button',
                      confirmButtonStyles.className
                    )}
                    onClick={cancelSubscription}
                    disabled={submitting}
                    shadowed
                  >
                    {submitting
                      ? 'Arrêt de l’assurance en cours…'
                      : 'Confirmer l’arrêt de l’assurance'}
                  </Button>
                )}
                <button className="cancel-button" onClick={closeOverlay}>
                  Annuler
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="canceled-title-valid item-spacing">
                Votre assurance a bien été stoppée
              </h2>
              <p className="canceled-text item-spacing">
                Merci de nous avoir fait confiance pour votre sécurité autour du
                globe.
              </p>
              <Button
                className={classnames(
                  'confirm-button',
                  confirmButtonStyles.className
                )}
                shadowed
                onClick={closeOverlay}
              >
                Retour au profil
              </Button>
            </>
          );
        }}
      </Overlay>
      <style jsx>{cancelSubscriptionStyles}</style>
      {confirmButtonStyles.styles}
      {cancelOverlayStyles.styles}
    </>
  );
};

CancelSubscription.propTypes = {
  subscription: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onCanceled: PropTypes.func.isRequired
};

export default CancelSubscription;
